/**
 * WorkstationsList — Ordenadores y portátiles (estándar de listados del CMDB).
 *
 * El andamiaje lo pone `common/ListPage.jsx`: buscador, filtros, exportar a
 * Excel, columnas, cebra, filas de grupo, clic en la fila para abrir la ficha
 * EN LECTURA y los botones `[acciones propias] · Copiar · Editar · Eliminar`.
 *
 * Lo propio de esta pantalla:
 *  · Filtros: Ubicación, Tipo (PC / portátil), Estado, Agente, SO, Sala/planta,
 *    Cifrado, Parches, Antivirus y Riesgo. Los cuatro últimos llegan también por
 *    query desde los enlaces del dashboard (`?bitlocker=unprotected`…).
 *  · **Sin fila de KPIs**: el resumen vive en el dashboard de Equipos. Aquí solo
 *    la tabla, para no robar alto en pantallas pequeñas.
 *  · **Agrupar por**: cualquier columna visible, con grupos plegables.
 *  · Acción propia: **Componentes** (chasis, monitor, periféricos…). La ficha del
 *    agente y el reescaneo los pinta `AgentStatusBadge` en su columna.
 *  · Se refresca sola: cada 30 s y con los eventos del socket (`agent:status`).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ComputerDesktopIcon,
  DeviceTabletIcon,
  CpuChipIcon,
  LinkIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import ComponentsModal from './ComponentsModal';
import PortMapModal from './PortMapModal';
import WorkstationForm from './WorkstationForm';
import AgentStatusBadge from './AgentStatusBadge';
import ReportDialog from './ReportDialog';
import HelpDot from '../academy/HelpDot';
import { useListView } from '../views/ViewsContext';
import { useSocket } from '../contexts/SocketContext';
import { buildLocationMap, locationPath, locationPathOptions } from '../utils/locationTree';
import { getServerFormStyle } from '../utils/serverFormStyle';
import { versionIsOlder as isVersionOlder } from '../utils/version';
import useNavOrder from '../hooks/useNavOrder';
import './AppModal.css';

const STATUS_META = {
  active:         { label: 'Activo',         badge: 'list-badge--on' },
  maintenance:    { label: 'Mantenimiento',  badge: 'list-badge--warn' },
  decommissioned: { label: 'Dado de baja',   badge: 'list-badge--off' },
  inactive:       { label: 'Inactivo',       badge: 'list-badge--off' },
};

const AGENT_META = {
  online:  { label: 'Online',     badge: 'list-badge--on' },
  offline: { label: 'Offline',    badge: 'list-badge--off' },
  unknown: { label: 'Sin agente', badge: '' },
};

/** Cómo se instaló el agente. `unknown` se pinta como «—» (sin dato). */
const CHANNEL_LABELS = { msi: 'MSI', exe: 'EXE', source: 'Código' };

/** `2.10.1` → [2, 10, 1], solo para ordenar la columna de versión. */
function versionParts(value) {
  return String(value || '')
    .split(/[^0-9]+/)
    .filter(Boolean)
    .slice(0, 4)
    .map(Number);
}

/** Filtro inicial desde la URL, solo si el valor está entre los permitidos. */
function initialFilter(value, allowed) {
  return value && allowed.includes(value) ? value : '';
}

function WorkstationsList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const socket = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();

  const [workstations, setWorkstations] = useState([]);
  // Última versión publicada en el servidor; sirve para marcar desfasados.
  const [latestAgentVersion, setLatestAgentVersion] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [bulkResetKey, setBulkResetKey] = useState(0);

  const [search,       setSearch]       = useState('');
  const [filterLoc,    setFilterLoc]    = useState('');
  // Los enlaces del dashboard llegan con ?agent=, ?type= o ?status=. Se
  // aplican al montar para que «ver los equipos sin agente» abra ya filtrado.
  const [filterType,   setFilterType]   = useState(() => initialFilter(searchParams.get('type'), ['pc', 'laptop']));
  const [filterStatus, setFilterStatus] = useState(() => initialFilter(searchParams.get('status'), Object.keys(STATUS_META)));
  const [filterAgent,  setFilterAgent]  = useState(() => initialFilter(searchParams.get('agent'), Object.keys(AGENT_META)));
  const [filterOs,     setFilterOs]     = useState(() => searchParams.get('os') || '');
  const [filterBitlocker, setFilterBitlocker] = useState(() => initialFilter(searchParams.get('bitlocker'), ['protected', 'unprotected', 'in_progress', 'not_applicable', 'unknown']));
  const [filterUpdates, setFilterUpdates] = useState(() => initialFilter(searchParams.get('updates'), ['ok', 'pending', 'critical', 'not_applicable', 'unknown']));
  const [filterAv,     setFilterAv]     = useState(() => initialFilter(searchParams.get('av'), ['protected', 'outdated', 'disabled', 'missing', 'not_applicable', 'unknown']));
  const [filterRisk,   setFilterRisk]   = useState(() => initialFilter(searchParams.get('risk'), ['critical', 'warning']));
  // «Solo desfasados» llega desde el dashboard (`?outdated=1`).
  const [filterOutdated, setFilterOutdated] = useState(() => (searchParams.get('outdated') === '1' ? '1' : ''));
  const [filterRoom,   setFilterRoom]   = useState('');
  // Equipos unidos o no a dominio (`part_of_domain`). El dashboard ya enlazaba
  // aquí; el filtro es lo que faltaba para «todos los equipos sin dominio».
  const [filterDomain, setFilterDomain] = useState('');
  const [showReport,   setShowReport]   = useState(false);
  const [groupBy,      setGroupBy]      = useState('');
  const [hlId,         setHlId]         = useState(null);

  const [locations,    setLocations]    = useState([]);
  const estiloFicha = getServerFormStyle();
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const rutaUbicacion = useCallback(
    (item) => (item?.location_id ? locationPath(mapaUbicaciones, item.location_id) : '') || item?.location_name || '',
    [mapaUbicaciones],
  );
  const [showForm,     setShowForm]     = useState(false);
  const [formMode,     setFormMode]     = useState('view');
  const [editItem,     setEditItem]     = useState(null);
  const [cloneSource,  setCloneSource]  = useState(null);
  const [componentsItem, setComponentsItem] = useState(null);
  const [portMapWS,    setPortMapWS]    = useState(null);

  const fetchWorkstations = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await api.getWorkstations({ company_id: companyId, include_connection: true });
      setWorkstations(res.data || []);
      setLatestAgentVersion(res.latestAgentVersion || null);
    } catch (err) { setError(err.message); }
    finally { if (!silent) setLoading(false); }
  }, [companyId]);

  // Resalta el elemento indicado por ?destacar=<id> (desde el Diseñador de
  // Servicios) y con `&abrir=1` abre además su ficha.
  useEffect(() => {
    if (loading || !workstations.length) return undefined;
    const raw = searchParams.get('destacar');
    if (!raw) return undefined;
    const id = Number(raw);
    const target = workstations.find((w) => w.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { openRead(target); return undefined; }
    setHlId(id);
    const t1 = setTimeout(() => {
      const el = document.getElementById(`ws-row-${id}`) || document.getElementById(`ws-card-${id}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 120);
    const t2 = setTimeout(() => setHlId(null), 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstations, loading, searchParams, setSearchParams]);

  useEffect(() => {
    if (companyId) {
      fetchWorkstations();
      api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
    }
  }, [companyId, fetchWorkstations]);

  useEffect(() => {
    if (!companyId) return undefined;
    const timerId = window.setInterval(() => {
      fetchWorkstations({ silent: true });
    }, 30000);
    return () => window.clearInterval(timerId);
  }, [companyId, fetchWorkstations]);

  useEffect(() => {
    if (!socket) return undefined;

    const onConnect = () => {
      fetchWorkstations({ silent: true });
    };
    const onAgentStatus = ({ id, ci_type, agent_status, last_seen_at }) => {
      if (ci_type && ci_type !== 'workstation') return;
      setWorkstations((prev) =>
        prev.map((ws) => (Number(ws.id) === Number(id) ? { ...ws, agent_status, last_seen_at } : ws))
      );
    };
    // Presencia en vivo: llega antes que el `agent:status` de la BD.
    const onAgentPresence = ({ agent_id, online, company_id }) => {
      if (!agent_id) return;
      if (company_id && companyId && Number(company_id) !== Number(companyId)) return;
      setWorkstations((prev) => prev.map((ws) => (
        String(ws.agent_id || '') === String(agent_id)
          ? { ...ws, agent_status: online ? 'online' : 'offline', last_seen_at: new Date().toISOString() }
          : ws
      )));
    };

    socket.on('connect', onConnect);
    socket.on('agent:status', onAgentStatus);
    socket.on('agent:presence', onAgentPresence);

    return () => {
      socket.off('connect', onConnect);
      socket.off('agent:status', onAgentStatus);
      socket.off('agent:presence', onAgentPresence);
    };
  }, [socket, fetchWorkstations, companyId]);

  const osOptions = useMemo(() => {
    const values = workstations.map((w) => [w.os_name, w.os_version].filter(Boolean).join(' '));
    return [...new Set(values.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
  }, [workstations]);
  const roomOptions = useMemo(() => {
    const values = workstations.map((w) => (w.room || '').trim());
    return [...new Set(values.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
  }, [workstations]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workstations.filter((w) => {
      const osText = [w.os_name, w.os_version].filter(Boolean).join(' ');
      const matchesSearch = !q
        || (w.code || '').toLowerCase().includes(q)
        || (w.name || '').toLowerCase().includes(q)
        || (w.room || '').toLowerCase().includes(q)
        || (w.assigned_to || '').toLowerCase().includes(q)
        || (w.mac_address || '').toLowerCase().includes(q)
        || (w.ip_address || '').toLowerCase().includes(q)
        || (w.cpu || '').toLowerCase().includes(q)
        || osText.toLowerCase().includes(q);
      const matchesLoc = !filterLoc || String(w.location_id) === filterLoc;
      const matchesType = !filterType || w.device_type === filterType;
      const matchesStatus = !filterStatus || w.status === filterStatus;
      const matchesAgent = !filterAgent
        || (filterAgent === 'unknown' ? (!w.agent_status || w.agent_status === 'unknown') : w.agent_status === filterAgent);
      const matchesOs = !filterOs || osText === filterOs;
      const matchesRoom = !filterRoom || (w.room || '').trim() === filterRoom;
      const matchesBitlocker = !filterBitlocker || (w.bitlocker_state || 'unknown') === filterBitlocker;
      const matchesUpdates = !filterUpdates || (w.updates_state || 'unknown') === filterUpdates;
      const matchesAv = !filterAv || (w.av_state || 'unknown') === filterAv;
      const matchesRisk = !filterRisk || (w.risk?.level || 'ok') === filterRisk;
      const matchesOutdated = filterOutdated !== '1'
        || (!!latestAgentVersion && isVersionOlder(w.agent_version, latestAgentVersion));
      const matchesDomain = !filterDomain
        || (filterDomain === 'yes' ? !!w.part_of_domain : !w.part_of_domain);
      return matchesSearch && matchesLoc && matchesType && matchesStatus
        && matchesAgent && matchesOs && matchesRoom
        && matchesBitlocker && matchesUpdates && matchesAv && matchesRisk
        && matchesOutdated && matchesDomain;
    });
  }, [workstations, search, filterLoc, filterType, filterStatus, filterAgent, filterOs, filterRoom,
      filterBitlocker, filterUpdates, filterAv, filterRisk, filterOutdated, filterDomain, latestAgentVersion]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navWorkstations = useNavOrder(filtered, editItem, showForm);

  const closeForm = () => {
    setShowForm(false);
    setEditItem(null);
    setCloneSource(null);
    fetchWorkstations();
  };

  /* ── Abrir ficha: LECTURA por defecto ─────────────────────────────────── */
  const openRead = (ws) => { setEditItem(ws); setFormMode('view'); setShowForm(true); };
  const openEdit = (ws) => { setEditItem(ws); setFormMode('edit'); setShowForm(true); };
  const openCreate = () => { setEditItem(null); setFormMode('edit'); setShowForm(true); };

  const handleDelete = async (ws) => {
    try {
      await api.deleteWorkstation(ws.id, companyId);
      await fetchWorkstations();
    } catch (err) {
      setError(err.message || tr('ws.delete_error', 'No se pudo eliminar el dispositivo'));
    }
  };

  /** Agrupación de la tabla: etiqueta del grupo según la columna elegida. */
  const groupLabelOf = useCallback((ws) => {
    if (!groupBy) return null;
    const location = locations.find((l) => l.id === ws.location_id);
    return getWsCellText(groupBy, ws, location, rutaUbicacion(ws)) || tr('list.no_value', 'Sin valor');
  }, [groupBy, locations, rutaUbicacion, tr]);

  /* ── Columnas ─────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    const statusBadge = (ws) => {
      const meta = STATUS_META[ws.status] || { label: ws.status || '—', badge: '' };
      return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
    };
    return [
      {
        key: 'type',
        label: tr('ws.type', 'Tipo'),
        sortable: true,
        sortValue: (ws) => (ws.device_type === 'laptop' ? 1 : 0),
        render: (ws) => {
          const WsIcon = ws.device_type === 'laptop' ? DeviceTabletIcon : ComputerDesktopIcon;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <WsIcon style={{ width: 16, height: 16, color: 'var(--text-secondary)' }} />
              <span className="list-hint">{ws.device_type === 'laptop' ? tr('ws.laptop', 'Portàtil') : tr('ws.pc', 'PC')}</span>
            </span>
          );
        },
      },
      {
        key: 'code',
        label: tr('list.code', 'Código'),
        sortable: true,
        render: (ws) => (ws.code ? <span className="list-code">{ws.code}</span> : dash),
      },
      {
        key: 'name',
        label: tr('list.name', 'Nombre'),
        sortable: true,
        render: (ws) => {
          const WsIcon = ws.device_type === 'laptop' ? DeviceTabletIcon : ComputerDesktopIcon;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <WsIcon style={{ width: 15, height: 15, color: 'var(--text-muted)' }} />
              {ws.name}
            </span>
          );
        },
      },
      { key: 'cpu', label: 'CPU', sortable: true, render: (ws) => ws.cpu || dash },
      {
        key: 'ram_gb',
        label: 'RAM (GB)',
        sortable: true,
        sortValue: (ws) => Number(ws.ram_gb || 0),
        render: (ws) => (ws.ram_gb ? `${ws.ram_gb} GB` : dash),
      },
      {
        key: 'os',
        label: 'SO',
        sortable: true,
        sortValue: (ws) => [ws.os_name, ws.os_version].filter(Boolean).join(' '),
        render: (ws) => ([ws.os_name, ws.os_version].filter(Boolean).join(' ') || dash),
      },
      { key: 'mac_address', label: 'MAC', sortable: true, render: (ws) => (ws.mac_address ? <span className="list-code">{ws.mac_address}</span> : dash) },
      { key: 'ip_address', label: 'IP', sortable: true, render: (ws) => (ws.ip_address ? <span className="list-code">{ws.ip_address}</span> : dash) },
      { key: 'assigned_to', label: tr('ws.assigned_to', 'Asignado a'), sortable: true, render: (ws) => ws.assigned_to || dash },
      {
        key: 'room',
        label: tr('ws.room', 'Sala / Planta'),
        sortable: true,
        sortValue: (ws) => `${ws.room || ''} ${ws.floor || ''}`.trim(),
        render: (ws) => (ws.room || ws.floor
          ? <span>{ws.room || ''}{ws.floor ? <span className="list-hint"> (P{ws.floor})</span> : null}</span>
          : dash),
      },
      {
        key: 'location',
        label: tr('list.location', 'Ubicación'),
        sortable: true,
        sortValue: (ws) => rutaUbicacion(ws),
        render: (ws) => rutaUbicacion(ws) || dash,
      },
      {
        key: 'status',
        label: tr('list.status', 'Estado'),
        sortable: true,
        sortValue: (ws) => ws.status || '',
        render: statusBadge,
      },
      {
        key: 'agent',
        label: tr('ws.agent', 'Agente'),
        sortable: true,
        sortValue: (ws) => ({ online: 0, offline: 1 }[ws.agent_status] ?? 2),
        render: (ws) => (
          <AgentStatusBadge
            status={ws.agent_status}
            lastSeenAt={ws.last_seen_at}
            agentId={ws.agent_id}
            companyId={companyId}
            ciType="workstation"
            ciId={ws.id}
            showRescan={ws.agent_status !== 'unknown'}
          />
        ),
      },
      {
        key: 'agent_version',
        label: tr('ws.agent_version', 'Versión del agente'),
        sortable: true,
        sortValue: (ws) => versionParts(ws.agent_version),
        render: (ws) => {
          if (!ws.agent_version) return dash;
          const outdated = isVersionOlder(ws.agent_version, latestAgentVersion);
          return (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
              title={outdated ? `${tr('ws.agent_latest', 'Última publicada')}: v${latestAgentVersion}` : undefined}
            >
              <span className={outdated ? 'list-badge list-badge--warn' : 'list-code'}>v{ws.agent_version}</span>
              {outdated && <span className="list-hint">→ v{latestAgentVersion}</span>}
            </span>
          );
        },
      },
      {
        key: 'install_channel',
        label: tr('ws.install_channel', 'Instalación'),
        sortable: true,
        sortValue: (ws) => ws.install_channel || '',
        render: (ws) => CHANNEL_LABELS[ws.install_channel]
          ? <span className="list-code">{CHANNEL_LABELS[ws.install_channel]}</span>
          : dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, locations, companyId, rutaUbicacion, latestAgentVersion]);

  const defaultVisible = [
    'type', 'code', 'name', 'cpu', 'ram_gb', 'os', 'ip_address', 'assigned_to',
    'room', 'location', 'status', 'agent', 'agent_version', 'install_channel',
  ];

  const exportRows = useMemo(() => filtered.map((ws) => {
    const location = locations.find((l) => l.id === ws.location_id);
    const record = {};
    columns.forEach((col) => { record[typeof col.label === 'string' ? col.label : col.key] = getWsCellText(col.key, ws, location, rutaUbicacion(ws)); });
    return record;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [filtered, locations, columns, rutaUbicacion]);

  const filters = [
    {
      key: 'location',
      label: tr('ws.filter_location', 'Ubicación'),
      allLabel: tr('ws.filter_location_all', 'Todas las ubicaciones'),
      value: filterLoc,
      onChange: setFilterLoc,
      options: locationPathOptions(locations),
    },
    {
      key: 'type',
      label: tr('ws.filter_type', 'Tipo'),
      allLabel: tr('ws.filter_type_all', 'Todos los tipos'),
      value: filterType,
      onChange: setFilterType,
      options: [
        { value: 'pc', label: tr('ws.pc', 'PC escritorio') },
        { value: 'laptop', label: tr('ws.laptop', 'Portátil') },
      ],
    },
    {
      key: 'status',
      label: tr('ws.filter_status', 'Estado'),
      allLabel: tr('ws.filter_status_all', 'Todos los estados'),
      value: filterStatus,
      onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'agent',
      label: tr('ws.filter_agent', 'Agente'),
      allLabel: tr('ws.filter_agent_all', 'Cualquier agente'),
      value: filterAgent,
      onChange: setFilterAgent,
      options: [
        { value: 'online', label: AGENT_META.online.label },
        { value: 'offline', label: AGENT_META.offline.label },
        { value: 'unknown', label: AGENT_META.unknown.label },
      ],
    },
    {
      key: 'os',
      label: 'SO',
      allLabel: tr('ws.filter_os_all', 'Cualquier SO'),
      value: filterOs,
      onChange: setFilterOs,
      options: osOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'room',
      label: tr('ws.filter_room', 'Sala / Planta'),
      allLabel: tr('ws.filter_room_all', 'Todas las salas'),
      value: filterRoom,
      onChange: setFilterRoom,
      options: roomOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'domain',
      label: 'Dominio',
      allLabel: 'Todos (con y sin dominio)',
      value: filterDomain,
      onChange: setFilterDomain,
      options: [
        { value: 'yes', label: 'Unidos a dominio' },
        { value: 'no', label: 'Sin dominio' },
      ],
    },
    {
      key: 'bitlocker',
      label: tr('ws.filter_bitlocker', 'Cifrado'),
      allLabel: tr('ws.filter_bitlocker_all', 'Cualquier cifrado'),
      value: filterBitlocker,
      onChange: setFilterBitlocker,
      options: [
        { value: 'protected', label: tr('ws.bl_protected', 'Cifrado') },
        { value: 'unprotected', label: tr('ws.bl_unprotected', 'Sin cifrar') },
        { value: 'in_progress', label: tr('ws.bl_progress', 'En proceso') },
        { value: 'unknown', label: tr('ws.bl_unknown', 'Sin datos') },
      ],
    },
    {
      key: 'updates',
      label: tr('ws.filter_updates', 'Parches'),
      allLabel: tr('ws.filter_updates_all', 'Cualquier estado'),
      value: filterUpdates,
      onChange: setFilterUpdates,
      options: [
        { value: 'ok', label: tr('ws.up_ok', 'Al día') },
        { value: 'pending', label: tr('ws.up_pending', 'Pendientes') },
        { value: 'critical', label: tr('ws.up_critical', 'Críticas') },
        { value: 'unknown', label: tr('ws.up_unknown', 'Sin datos') },
      ],
    },
    {
      key: 'av',
      label: tr('ws.filter_av', 'Antivirus'),
      allLabel: tr('ws.filter_av_all', 'Cualquier antivirus'),
      value: filterAv,
      onChange: setFilterAv,
      options: [
        { value: 'protected', label: tr('ws.av_protected', 'Activo y al día') },
        { value: 'outdated', label: tr('ws.av_outdated', 'Firmas antiguas') },
        { value: 'disabled', label: tr('ws.av_disabled', 'Deshabilitado') },
        { value: 'missing', label: tr('ws.av_missing', 'Sin antivirus') },
        { value: 'unknown', label: tr('ws.av_unknown', 'Sin datos') },
      ],
    },
    {
      key: 'risk',
      label: tr('ws.filter_risk', 'Riesgo'),
      allLabel: tr('ws.filter_risk_all', 'Cualquier riesgo'),
      value: filterRisk,
      onChange: setFilterRisk,
      options: [
        { value: 'critical', label: tr('ws.risk_critical', 'Crítico') },
        { value: 'warning', label: tr('ws.risk_warning', 'Aviso') },
      ],
    },
    {
      key: 'outdated',
      label: tr('ws.filter_agent_version', 'Versión del agente'),
      allLabel: tr('ws.filter_agent_version_all', 'Cualquier versión'),
      value: filterOutdated,
      onChange: setFilterOutdated,
      options: [
        { value: '1', label: tr('ws.only_outdated', 'Solo desfasados') },
      ],
    },
    {
      key: 'group',
      label: tr('ws.group_by', 'Agrupar por'),
      allLabel: tr('ws.group_none', 'Sin agrupar'),
      value: groupBy,
      onChange: setGroupBy,
      options: columns.map((col) => ({ value: col.key, label: col.label })),
    },
  ];

  const subtitle = `${workstations.length} ${tr('ws.plural', 'dispositivos')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  /** Tarjetas: lo esencial del dispositivo, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((ws) => {
          const locationName = rutaUbicacion(ws);
          const meta = STATUS_META[ws.status] || { label: ws.status || '—', badge: '' };
          return (
            <div
              key={ws.id}
              id={`ws-card-${ws.id}`}
              className={`list-card${ws.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openRead(ws)}
              onKeyDown={(event) => { if (event.key === 'Enter') openRead(ws); }}
              title={`${tr('action.view', 'Ver')} ${ws.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">
                    {ws.device_type === 'laptop'
                      ? <DeviceTabletIcon style={{ width: 16, height: 16, verticalAlign: 'middle', marginRight: 4 }} />
                      : <ComputerDesktopIcon style={{ width: 16, height: 16, verticalAlign: 'middle', marginRight: 4 }} />
                    }{ws.name}
                  </div>
                  {ws.code && <span className="list-code">{ws.code}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                {ws.cpu && <span>🔲 {ws.cpu}</span>}
                {ws.ram_gb && <span>💾 {ws.ram_gb} GB</span>}
                {(ws.os_name || ws.os_version) && <span>🖥 {[ws.os_name, ws.os_version].filter(Boolean).join(' ')}</span>}
                {ws.ip_address && <span className="list-code">{ws.ip_address}</span>}
                {ws.assigned_to && <span>👤 {ws.assigned_to}</span>}
                {(ws.room || ws.floor) && <span>📍 {ws.room || ''}{ws.floor ? ` P${ws.floor}` : ''}</span>}
                {locationName && <span>🏢 {locationName}</span>}
              </div>

              <div>
                <AgentStatusBadge
                  status={ws.agent_status}
                  lastSeenAt={ws.last_seen_at}
                  agentId={ws.agent_id}
                  companyId={companyId}
                  ciType="workstation"
                  ciId={ws.id}
                  showRescan={ws.agent_status !== 'unknown'}
                />
              </div>

              <div className="list-card-actions">{rowActions(ws)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Vistas ───────────────────────────────────────────────────────────── */
  const WS_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ws.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ws.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ws.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ws.view_cards_hint', 'Tarjetas con lo esencial de cada dispositivo'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, locations, hlId, companyId, rutaUbicacion]);
  useListView('workstations', 'list');

  if (!companyId) return (
    <div className="list-page" style={{ padding: '1.5rem' }}>
      <div className="alert alert-warning">{tr('ws.select_company', 'Selecciona una empresa para ver los dispositivos.')}</div>
    </div>
  );

  return (
    <>
      {showForm && (
        <WorkstationForm
          workstation={cloneSource || editItem}
          companyId={companyId}
          initialMode={formMode}
          variant={estiloFicha === 'cards' ? 'cards' : 'classic'}
          latestAgentVersion={latestAgentVersion}
          navItems={navWorkstations}
          navIndex={editItem ? navWorkstations.findIndex(w => String(w.id) === String(editItem.id)) : -1}
          onNavigate={(destino) => setEditItem(destino)}
          onClose={closeForm}
          onSave={(creado) => {
            fetchWorkstations();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setEditItem(creado); setFormMode('edit'); }
          }}
        />
      )}

      <ReportDialog
        open={showReport}
        onClose={() => setShowReport(false)}
        title="Informe de ordenadores y portátiles"
        subtitle={`${filtered.length} equipo${filtered.length === 1 ? '' : 's'} · ${new Date().toLocaleDateString('es-ES')}`}
        filters={filters
          .filter((f) => f.key !== 'group' && f.value)
          .map((f) => ({
            label: f.label,
            value: (f.options || []).find((o) => String(o.value) === String(f.value))?.label || f.value,
          }))
          .concat(search.trim() ? [{ label: 'Búsqueda', value: search.trim() }] : [])}
        columns={columns.map((c) => (typeof c.label === 'string' ? c.label : c.key))}
        rows={exportRows}
        fileBase="informe-ordenadores"
        sheetName="Informe"
      />

      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={ComputerDesktopIcon}
          title={tr('ws.title', 'Ordenadores y portátiles')}
          subtitle={subtitle}
          onCreate={openCreate}
          createLabel={tr('ws.new', 'Nuevo dispositivo')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('ws.search_placeholder', 'Buscar por código, nombre, sala, usuario, IP, CPU…')}
          filters={filters}
          exportRows={exportRows}
          exportFileBase="workstations"
          exportSheetName="Ordenadores"
          headerActions={(
            <>
              <HelpDot topic="listados.workstations" label="Ayuda: el listado de ordenadores" />
              <button
                type="button"
                className="wol-btn-secondary"
                onClick={() => setShowReport(true)}
                disabled={!filtered.length}
                title="Genera un informe imprimible (PDF) o Excel con los filtros aplicados"
              >
                Informe
              </button>
            </>
          )}

          columns={columns}
          columnStorageKey="cmdb_cols_workstations"
          companyId={companyId}
          importEntity="workstations"
          onImported={() => fetchWorkstations()}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(ws) => ws.id}
          rowId={(ws) => `ws-row-${ws.id}`}
          rowClassName={(ws) => (ws.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openRead}
          rowTitle={(ws) => `${tr('action.view', 'Ver')} ${ws.name}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="workstations"
                supportsRack={false}
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchWorkstations();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="workstations"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchWorkstations();
                }}
              />
            </>
          )}
          groupBy={groupBy ? groupLabelOf : undefined}

          views={WS_VIEWS}
          listKey="workstations"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando dispositivos…')}
          error={error}

          empty={{
            icon: '🖥️',
            title: workstations.length === 0
              ? tr('ws.empty', 'No hay ordenadores registrados')
              : tr('msg.no_results', 'Ningún dispositivo coincide con los filtros'),
            subtitle: workstations.length === 0 ? tr('ws.empty_hint', 'Crea el primero para empezar a inventariar.') : undefined,
            action: workstations.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('ws.new', 'Nuevo dispositivo')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (ws) => (
              <>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => setPortMapWS(ws)}
                  title={tr('ws.ports', 'Puertos y conexiones')}
                >
                  <LinkIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => setComponentsItem(ws)}
                  title={tr('ws.components', 'Componentes (chasis, monitor, periféricos…)')}
                >
                  <CpuChipIcon />
                </button>
              </>
            ),
            onCopy: (ws) => {
              setCloneSource({
                ...ws,
                id: undefined,
                code: `${ws.code}-copia`,
                name: `${ws.name} (copia)`,
                mac_address: '',
                ip_address: '',
                serial_number: '',
              });
              setEditItem(null);
              setFormMode('edit');
              setShowForm(true);
            },
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('ws.delete_title', 'Eliminar dispositivo'),
            deleteMessage: (ws) => (
              <>
                {tr('ws.delete_confirm', '¿Seguro que quieres eliminar el dispositivo')} <strong>{ws.name}</strong>?{' '}
                {tr('ws.delete_hint', 'Se eliminarán también sus componentes y conexiones. No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* Diálogo: componentes */}
      {componentsItem && (
        <ComponentsModal
          element={componentsItem}
          companyId={companyId}
          onClose={() => setComponentsItem(null)}
        />
      )}

      {/* Diálogo: mapa de puertos */}
      {portMapWS && (
        <PortMapModal
          element={{
            id: portMapWS.id,
            name: portMapWS.name,
            code: portMapWS.code,
            type: 'workstation',
            ports: portMapWS.ports || [],
          }}
          companyId={companyId}
          onClose={() => setPortMapWS(null)}
          onUpdated={() => { setPortMapWS(null); fetchWorkstations(); }}
        />
      )}
    </>
  );
}
function getWsCellText(key, ws, location, ruta = '') {
  const STATUS_LABELS = { active: 'Activo', maintenance: 'Mantenimiento', decommissioned: 'Dado de baja', inactive: 'Inactivo' };
  switch (key) {
    case 'type':
      return ws.device_type === 'laptop' ? 'Portatil' : 'PC';
    case 'code':
      return ws.code || '';
    case 'name':
      return ws.name || '';
    case 'cpu':
      return ws.cpu || '';
    case 'ram_gb':
      return ws.ram_gb ? `${ws.ram_gb} GB` : '';
    case 'os':
      return `${ws.os_name || ''}${ws.os_version ? ` ${ws.os_version}` : ''}`.trim();
    case 'mac_address':
      return ws.mac_address || '';
    case 'ip_address':
      return ws.ip_address || '';
    case 'assigned_to':
      return ws.assigned_to || '';
    case 'room':
      return `${ws.room || ''}${ws.floor ? ` (P${ws.floor})` : ''}`.trim();
    case 'location':
      return ruta || location?.name || '';
    case 'status':
      return STATUS_LABELS[ws.status] || ws.status || '';
    case 'agent':
      return ws.agent_status || 'unknown';
    case 'agent_version':
      return ws.agent_version ? `v${ws.agent_version}` : '';
    case 'install_channel':
      return CHANNEL_LABELS[ws.install_channel] || '';
    default:
      return ws[key] ?? '';
  }
}

export default WorkstationsList;
